import React, { useState, useEffect } from "react";
import Input from "./Input";
import Select from "./Select";

const AddressForm = ({
  initialData = {},
  onChange,
  errors = {},
  disabled = false,
  showLabel = true,
  className = "",
}) => {
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    pinCode: "",
    landmark: "",
    country: "India",
    ...initialData,
  });

  // Indian states with codes
  const stateOptions = [
    { value: "AN", label: "Andaman and Nicobar Islands" },
    { value: "AP", label: "Andhra Pradesh" },
    { value: "AR", label: "Arunachal Pradesh" },
    { value: "AS", label: "Assam" },
    { value: "BR", label: "Bihar" },
    { value: "CH", label: "Chandigarh" },
    { value: "CT", label: "Chhattisgarh" },
    { value: "DN", label: "Dadra and Nagar Haveli and Daman and Diu" },
    { value: "DL", label: "Delhi" },
    { value: "GA", label: "Goa" },
    { value: "GJ", label: "Gujarat" },
    { value: "HR", label: "Haryana" },
    { value: "HP", label: "Himachal Pradesh" },
    { value: "JK", label: "Jammu and Kashmir" },
    { value: "JH", label: "Jharkhand" },
    { value: "KA", label: "Karnataka" },
    { value: "KL", label: "Kerala" },
    { value: "LA", label: "Ladakh" },
    { value: "LD", label: "Lakshadweep" },
    { value: "MP", label: "Madhya Pradesh" },
    { value: "MH", label: "Maharashtra" },
    { value: "MN", label: "Manipur" },
    { value: "ML", label: "Meghalaya" },
    { value: "MZ", label: "Mizoram" },
    { value: "NL", label: "Nagaland" },
    { value: "OR", label: "Odisha" },
    { value: "PY", label: "Puducherry" },
    { value: "PB", label: "Punjab" },
    { value: "RJ", label: "Rajasthan" },
    { value: "SK", label: "Sikkim" },
    { value: "TN", label: "Tamil Nadu" },
    { value: "TG", label: "Telangana" },
    { value: "TR", label: "Tripura" },
    { value: "UP", label: "Uttar Pradesh" },
    { value: "UT", label: "Uttarakhand" },
    { value: "WB", label: "West Bengal" },
  ];

  // Common city suggestions based on state
  const citySuggestions = {
    MH: [
      "Mumbai",
      "Pune",
      "Nagpur",
      "Nashik",
      "Aurangabad",
      "Thane",
      "Kalyan",
      "Navi Mumbai",
    ],
    DL: [
      "New Delhi",
      "Delhi",
      "North Delhi",
      "South Delhi",
      "East Delhi",
      "West Delhi",
    ],
    KA: [
      "Bangalore",
      "Mysore",
      "Mangalore",
      "Hubli",
      "Belgaum",
      "Gulbarga",
      "Shimoga",
    ],
    TN: [
      "Chennai",
      "Coimbatore",
      "Madurai",
      "Tiruchirappalli",
      "Salem",
      "Tiruppur",
    ],
    GJ: [
      "Ahmedabad",
      "Surat",
      "Vadodara",
      "Rajkot",
      "Bhavnagar",
      "Jamnagar",
      "Gandhinagar",
    ],
    WB: ["Kolkata", "Howrah", "Durgapur", "Asansol", "Siliguri", "Bardhaman"],
    UP: [
      "Lucknow",
      "Kanpur",
      "Ghaziabad",
      "Agra",
      "Meerut",
      "Varanasi",
      "Allahabad",
      "Noida",
    ],
    RJ: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner", "Alwar"],
    TG: ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar", "Ramagundam"],
    AP: [
      "Visakhapatnam",
      "Vijayawada",
      "Guntur",
      "Nellore",
      "Kurnool",
      "Tirupati",
    ],
  };

  useEffect(() => {
    if (onChange) {
      onChange(address);
    }
  }, [address]);

  const handleChange = (field, value) => {
    const updatedAddress = { ...address, [field]: value };

    // Auto-detect city tier based on city
    if (field === "city") {
      const metroCities = [
        "Mumbai",
        "Delhi",
        "New Delhi",
        "Bangalore",
        "Chennai",
        "Kolkata",
        "Hyderabad",
        "Pune",
        "Ahmedabad",
      ];
      const tier1Cities = [
        "Surat",
        "Jaipur",
        "Lucknow",
        "Kanpur",
        "Nagpur",
        "Indore",
        "Thane",
        "Bhopal",
        "Visakhapatnam",
        "Vadodara",
      ];

      if (metroCities.includes(value)) {
        updatedAddress.cityTier = "METRO";
      } else if (tier1Cities.includes(value)) {
        updatedAddress.cityTier = "TIER1";
      } else {
        updatedAddress.cityTier = "TIER2";
      }
    }

    setAddress(updatedAddress);
  };

  const validatePinCode = (pinCode) => {
    return /^[1-9][0-9]{5}$/.test(pinCode);
  };

  const getStateName = (stateCode) => {
    const state = stateOptions.find((opt) => opt.value === stateCode);
    return state ? state.label : stateCode;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {showLabel && (
        <h3 className="text-lg font-medium text-gray-900">Address Details</h3>
      )}

      <div className="grid grid-cols-1 gap-4">
        <Input
          label="Address Line 1"
          value={address.line1}
          onChange={(e) => handleChange("line1", e.target.value)}
          error={errors.line1}
          placeholder="Building/Flat No., Street Name"
          required
          disabled={disabled}
        />

        <Input
          label="Address Line 2"
          value={address.line2}
          onChange={(e) => handleChange("line2", e.target.value)}
          error={errors.line2}
          placeholder="Area, Colony, Landmark (Optional)"
          disabled={disabled}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="State"
            value={address.state}
            onChange={(e) => handleChange("state", e.target.value)}
            options={stateOptions}
            error={errors.state}
            placeholder="Select State"
            required
            disabled={disabled}
          />

          <div>
            <Input
              label="City"
              value={address.city}
              onChange={(e) => handleChange("city", e.target.value)}
              error={errors.city}
              placeholder="Enter City"
              required
              disabled={disabled}
              list="city-suggestions"
            />
            {address.state && citySuggestions[address.state] && (
              <datalist id="city-suggestions">
                {citySuggestions[address.state].map((city) => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="PIN Code"
            value={address.pinCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              handleChange("pinCode", value);
            }}
            error={errors.pinCode}
            placeholder="6-digit PIN Code"
            maxLength="6"
            required
            disabled={disabled}
          />

          <Input
            label="Landmark"
            value={address.landmark}
            onChange={(e) => handleChange("landmark", e.target.value)}
            error={errors.landmark}
            placeholder="Near landmark (Optional)"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Address Preview */}
      {(address.line1 || address.city || address.state) && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-1">
            Address Preview:
          </p>
          <p className="text-sm text-gray-600">
            {address.line1 && `${address.line1}, `}
            {address.line2 && `${address.line2}, `}
            {address.city && `${address.city}, `}
            {address.state && `${getStateName(address.state)}, `}
            {address.pinCode && `PIN: ${address.pinCode}`}
            {address.landmark && ` (Near: ${address.landmark})`}
          </p>
        </div>
      )}
    </div>
  );
};

export default AddressForm;
